import * as moment from 'moment';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Client, Item, Organization } from '../shared/sdk/models';

import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ClientAddComponent } from '../clients/client-add/client-add.component';
import { HeadMenuService } from '../head/head-menu.service';
import { ItemAddComponent } from '../items/item-add/item-add.component';
import { OrganizationService } from '../organizations/organization.service';
import { SDKService } from '../sdk.service';
import { LoggerService } from '../shared/sdk/services/custom/logger.service';
import { LanguageService } from '../shared/services/language.service';
import { MyDpDateService } from '../shared/services/my-dp-date.service';
import { TitleService } from '../shared/services/title.service';
import { WhiteLabelService } from '../shared/services/whitelabel.service';
import { Sidebar2Service } from '../sidebar/sidebar2.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  activeOrg: Organization;
  activeOrgId: string;
  chartData: any;
  chartForm: FormGroup;
  chartOptions: any;
  currentLang: string;
  customColors: any;
  defaultCurrency: string;
  documents: any[];
  hasAnyDocuments: boolean;
  isSDK: Observable<boolean>;
  limit: number;
  loading: any;
  maxDate: NgbDateStruct;
  moment: any;
  page: number;
  periodTotal: any;
  thisMonth = {
    first: moment().startOf('month').format(),
    last: moment().endOf('month').format(),
  };
  today = moment().format();
  totalItems: number;
  totalStats: any;
  typeName: string;
  typeNamePlural: string;
  view: any[];
  private onDestroy$: Subject<void> = new Subject();

  constructor(
    public WL: WhiteLabelService,
    private dateService: MyDpDateService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private headMenuService: HeadMenuService,
    private languageService: LanguageService,
    private log: LoggerService,
    private modalService: NgbModal,
    private myDpDateService: MyDpDateService,
    private orgService: OrganizationService,
    private route: ActivatedRoute,
    private sidebarService: Sidebar2Service,
    private translate: TranslateService,
    private titleService: TitleService,
    private sdkService: SDKService,
  ) {}

  ngAfterViewInit(): void {
    this.sdkService.sendMessage('DASHBOARD_AFTER_VIEW_INIT');
  }

  ngOnInit(): void {
    this.log.info('Dashboard: ngOnInit');
    this.titleService.set(['Dashboard']);

    this.isSDK = this.sdkService.isSDK$.pipe(takeUntil(this.onDestroy$));

    // If no documents show welcome banner
    this.route.data.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
      const { hasDocuments } = data;
      this.hasAnyDocuments =
        typeof hasDocuments !== 'undefined' ? !!hasDocuments : true;
    });

    // Just for document pdf open / download
    this.languageService.current
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((lang) => (this.currentLang = lang));

    this.page = 1;
    this.limit = 5;
    this.totalItems = 0;
    this.loading = {
      documents: true,
      stats: true,
      chart: true,
    };

    this.chartOptions = {
      colorScheme: {
        domain: ['var(--blue)', 'var(--purple)', 'var(--indigo)'],
      },
      showXAxis: true,
      showYAxis: true,
    };
    this.maxDate = this.dateService.generate(moment().add(1, 'day'));
    this.moment = moment;
    this.typeName = 'invoice';
    this.typeNamePlural = 'invoices';

    // Chart form
    this.chartForm = this.fb.group({
      type: [, Validators.required],
      period: [],
      from: [, Validators.required],
      to: [, Validators.required],
      dist: [, Validators.required],
    });

    this.orgService.active
      .pipe(
        filter((org) => !!org),
        takeUntil(this.onDestroy$),
      )
      .subscribe(
        (org: Organization) => {
          this.activeOrg = org;
          this.activeOrgId = this.activeOrg.id;

          const defaultCurrencyDef = this.activeOrg._defaults.find(
            (d) => d.name === 'currencyId',
          );
          this.defaultCurrency = defaultCurrencyDef
            ? defaultCurrencyDef.value
            : 'EUR';

          this.resetData();
          this.resetChartForm();

          this.getStats(this.activeOrg.id);
          this.getTotalStats(this.activeOrg.id);
        },
        () => {
          this.loading.stats = false;
        },
      );

    this.headMenuService.set('dashboard', [
      {
        name: 'New invoice',
        id: 'mi-new-invoice',
        path: './../../documents/o/add/invoice',
        class: 'fe fe-plus-circle',
        namespace: 'documents/o/add/invoice',
      },
      {
        name: 'New estimate',
        path: './../../documents/o/add/estimate',
        class: 'fe fe-plus-circle',
        namespace: 'documents/o/add/estimate',
      },
      {
        name: 'button.new-expense',
        path: './../../documents/i/add/invoice',
        class: 'fe fe-plus-circle',
        namespace: 'documents/i/add/invoice',
      },
      {
        name: 'New client',
        class: 'fe fe-plus-circle',
        func: () => this.onShowClientAddModal(),
        namespace: 'clients/add',
      },
      {
        name: 'button.new-service-item',
        class: 'fe fe-plus-circle',
        func: () => this.onShowItemAddModal(),
        namespace: 'items/add',
      },
      {
        // name: 'refresh',
        class: 'far fa-sync-alt',
        tooltip: this.translate.get('tooltip.refresh'),
        func: () => {
          this.getTotalStats(this.activeOrg.id, true);
        },
        class_li: 'ml-auto',
        namespace: 'refresh',
      },
    ]);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  createLink(path: any[], params: any) {
    return this.sidebarService.generateLink(path, params);
  }

  getTotalStats(orgId: string, force = false) {
    this.log.info('Dashboard: getTotalStats');

    this.loading.stats = true;

    this.orgService.getTotalStats(orgId, force).subscribe(
      (stats) => {
        this.totalStats = stats;
        this.loading.stats = false;
        this.cdr.markForCheck();
      },
      (err) => {
        this.loading.stats = false;
        this.cdr.markForCheck();
      },
    );
  }

  getStats(orgId: string, force = false) {
    this.loading.chart = true;

    // TODO: Not precise enough
    const type = this.chartForm.controls.type.value;
    const from = moment(
      this.myDpDateService.parse(this.chartForm.controls.from.value),
    ).format('Y-MM-DD');
    const to = moment(
      this.myDpDateService.parse(this.chartForm.controls.to.value),
    ).format('Y-MM-DD');
    const dist = this.chartForm.controls.dist.value;

    if (this.chartForm.valid) {
      if (type !== 'payments') {
        const typeSplit = type.split('-');
        const gross = typeSplit[1] !== 'net';
        const incoming = typeSplit[1] === 'inc' || typeSplit[2] === 'inc';
        // console.log(typeSplit);
        // console.log(incoming);

        this.orgService
          .getDocumentValueStats(
            orgId,
            typeSplit[0],
            from,
            to,
            dist,
            gross,
            incoming,
            force,
          )
          .subscribe((data) => {
            this.loading.chart = false;
            this.periodTotal = data.total;
            this.chartData = data.stats;
            this.customColors = data.customColors;
            this.chartForm.markAsPristine();
            this.cdr.markForCheck();
          });
      } else {
        this.orgService
          .getPaymentValueStats(orgId, from, to, dist, force)
          .subscribe((data) => {
            this.loading.chart = false;
            this.periodTotal = data.total;
            this.chartData = data.stats;
            this.customColors = data.customColors;
            this.chartForm.markAsPristine();

            // Reset if graph has any data
            // Has documents only counts outgoing documents while this returns
            // payments for both.
            this.hasAnyDocuments =
              this.hasAnyDocuments || !!this.periodTotal.length;

            this.cdr.markForCheck();
          });
      }
    }
  }

  disableDay = (
    date: NgbDateStruct,
    current: { year: number; month: number },
  ) => {
    const dateM = this.dateService.generate(moment());
    return (
      date.year === dateM.year &&
      date.month === dateM.month &&
      date.day > dateM.day
    );
    // tslint:disable-next-line:semicolon
  };

  onChangeStartDate(date: NgbDateStruct) {
    const endDateCtrl = this.chartForm.controls.to;

    if (moment(endDateCtrl.value).isBefore(date)) {
      endDateCtrl.patchValue(date);
    }

    this.cdr.markForCheck();
  }

  onChangeEndDate(date: NgbDateStruct) {
    const startDateCtrl = this.chartForm.controls.from;

    if (moment(startDateCtrl.value).isAfter(date)) {
      startDateCtrl.patchValue(date);
    }

    this.cdr.markForCheck();
  }

  onChangeDate() {
    this.chartForm.controls.period.setValue('custom');
    this.chartForm.markAsDirty();
    this.cdr.markForCheck();
  }

  onChangePeriod() {
    this.log.log('Dashboard: onChangePeriod');
    let from;
    let to;

    switch (this.chartForm.controls.period.value) {
      case 'today':
        this.chartForm.controls.dist.setValue('day');
        from = moment();
        to = moment().endOf('day');
        break;

      case 'yesterday':
        this.chartForm.controls.dist.setValue('day');
        from = moment().subtract(1, 'day');
        to = moment().subtract(1, 'day').endOf('day');
        break;

      case 'week-to-date':
        this.chartForm.controls.dist.setValue('day');
        from = moment().subtract(1, 'week').startOf('day');
        to = moment().endOf('day');
        break;

      case 'previous-week':
        this.chartForm.controls.dist.setValue('day');
        from = moment().subtract(1, 'week').startOf('week');
        to = moment().subtract(1, 'week').endOf('week').endOf('day');
        break;

      case 'month-to-date':
        this.chartForm.controls.dist.setValue('day');
        from = moment().subtract(1, 'month');
        to = moment().endOf('day');
        break;

      case 'previous-month':
        this.chartForm.controls.dist.setValue('day');
        from = moment().subtract(1, 'month').startOf('month');
        to = moment().subtract(1, 'month').endOf('month').endOf('day');
        break;

      case 'half-year-to-date':
        this.chartForm.controls.dist.setValue('month');
        from = moment().subtract(6, 'months');
        to = moment().endOf('day');
        break;

      case 'this-year':
        this.chartForm.controls.dist.setValue('month');
        from = moment().startOf('year').startOf('month').startOf('day');
        to = moment().endOf('day');
        break;

      case 'year-to-date':
        this.chartForm.controls.dist.setValue('month');
        from = moment().subtract(1, 'year');
        to = moment().endOf('day');
        break;

      case 'previous-year':
        this.chartForm.controls.dist.setValue('month');
        from = moment().subtract(1, 'year').startOf('year');
        to = moment().subtract(1, 'year').endOf('year').endOf('day');
        break;

      case 'custom':
        // TODO: Open picker
        from = this.myDpDateService.parse(
          this.chartForm.controls.from.value,
          'moment',
        );
        to = this.myDpDateService.parse(
          this.chartForm.controls.to.value,
          'moment',
        );
        break;
    }

    this.chartForm.controls.from.setValue(this.myDpDateService.generate(from));
    this.chartForm.controls.to.setValue(this.myDpDateService.generate(to));
    this.cdr.markForCheck();
  }

  onShowClientAddModal() {
    const modalRef = this.modalService.open(ClientAddComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.client = new Client();
    modalRef.result.catch(() => {});
  }

  onShowItemAddModal() {
    const modalRef = this.modalService.open(ItemAddComponent);
    modalRef.componentInstance.item = new Item();
    modalRef.result.catch(() => {});
  }

  private resetChartForm() {
    this.chartForm.reset();

    const startOfMonth = moment().startOf('month');
    const today = moment();
    const dist = 'day';

    this.chartForm.setValue({
      type: 'payments',
      period: 'this-month',
      from: this.myDpDateService.generate(startOfMonth),
      to: this.myDpDateService.generate(today),
      dist,
    });

    this.cdr.markForCheck();
  }

  private resetData() {
    this.loading.stats = false;
    this.documents = [];
    this.chartData = [];
    this.customColors = [];
    this.periodTotal = [];
    this.cdr.markForCheck();
  }
}
